import React, {useMemo} from 'react';
import root from 'window-or-global';
import MuiLink, {LinkProps as MuiLinkProps} from '@material-ui/core/Link';
import {HashLink, NavHashLink, NavHashLinkProps} from 'react-router-hash-link';
import {generateRoutePath} from '@epic-core/common';
import withRouterRef from '../../../hocs/withRouterRef';
import {LocationDescriptor} from 'history';

type NavHashLinkKeys = 'exact' | 'smooth' | 'scroll' | 'isActive' | 'activeClassName' | 'to';

export interface LinkProps extends MuiLinkProps, Pick<NavHashLinkProps, NavHashLinkKeys> {
    to: string;
    resetScroll?: boolean;
    dontIncludeLocale?: boolean;
    isExternalUrl?: boolean;
}

const Link = withRouterRef<LinkProps>(
    ({
        staticContext,
        history,
        location,
        match,
        exact,
        smooth,
        scroll,
        isActive,
        activeClassName,
        to,
        resetScroll = false,
        dontIncludeLocale = false,
        isExternalUrl = false,
        forwardRef,
        role = 'link',
        children,
        ...rest
    }) => {
        const linkProps = useMemo(() => {
            if (isExternalUrl || /^[\w.]+:\/\//.test(to)) {
                const isHostUrlReg = new RegExp(
                    `^(?:https?://(?:www\\.)?)?epicgames\\.com${root.appContext}`,
                    'i'
                );

                if (isHostUrlReg.test(to)) {
                    return {
                        href: generateRoutePath(to)
                    };
                } else {
                    return {
                        rel: 'noopener noreferrer',
                        target: rest.target || '_blank',
                        href: to
                    };
                }
            }

            const routePath = generateRoutePath(to, dontIncludeLocale);
            const toWithState: LocationDescriptor<{resetScroll: boolean}> = {
                pathname: routePath,
                state: {resetScroll}
            };

            const hasQuery = /\?/.test(to);
            if (hasQuery) {
                toWithState.pathname = routePath.substring(0, routePath.indexOf('?'));
                toWithState.search = routePath.substring(routePath.indexOf('?'));
            }

            const _linkProps = {
                component: HashLink,
                to: resetScroll ? toWithState : generateRoutePath(to, dontIncludeLocale),
                role,
                smooth,
                scroll
            };

            if (activeClassName) {
                Object.assign(_linkProps, {
                    component: NavHashLink,
                    isActive,
                    activeClassName,
                    exact
                });
            }

            return _linkProps;
        }, [to, activeClassName, dontIncludeLocale]);

        return (
            <MuiLink innerRef={forwardRef} {...rest} {...linkProps}>
                {children}
            </MuiLink>
        );
    }
);

export default Link;
